import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import * as repo from '@/db/repositories'
import { createId } from '@/utils/id'
import type { Shelf } from '@/types/domain'

/**
 * Store de estanterías personalizadas del usuario.
 *
 * Cada estantería es un grupo many-to-many de libros. Las mutaciones
 * SIEMPRE reasignan `shelves.value` de forma inmutable (nunca mutan un
 * elemento in-place): el puente de sincronización CRDT
 * (`connectCollection`) no usa `deep:true`, así que solo detecta
 * reasignaciones del ref. Esto es coherente con cómo opera `summaries`.
 */
export const useShelvesStore = defineStore('shelves', () => {
  const shelves = ref<Shelf[]>([])
  const loading = ref(false)

  async function loadAll() {
    loading.value = true
    try {
      shelves.value = await repo.getAllShelves()
    } finally {
      loading.value = false
    }
  }

  /** Crea una estantería nueva y la persiste. */
  async function create(name: string, opts?: { description?: string | null; color?: string | null }): Promise<Shelf> {
    const now = new Date().toISOString()
    const shelf: Shelf = {
      id: createId(),
      name: name.trim(),
      description: opts?.description ?? null,
      color: opts?.color ?? null,
      bookIds: [],
      createdAt: now,
      updatedAt: now,
    }
    await repo.saveShelf(shelf)
    shelves.value = [shelf, ...shelves.value]
    return shelf
  }

  /** Reemplaza la lista entera (lo usa el puente CRDT al llegar cambios remotos). */
  function replaceAll(items: Shelf[]) {
    shelves.value = items.slice()
  }

  /** Helper interno: actualiza una estantería inmutablemente y persiste. */
  async function patchShelf(id: string, patch: Partial<Shelf>): Promise<void> {
    const idx = shelves.value.findIndex((s) => s.id === id)
    if (idx < 0) return
    const updated: Shelf = { ...shelves.value[idx], ...patch, updatedAt: new Date().toISOString() }
    await repo.saveShelf(updated)
    shelves.value = [
      ...shelves.value.slice(0, idx),
      updated,
      ...shelves.value.slice(idx + 1),
    ]
  }

  async function rename(id: string, name: string) {
    await patchShelf(id, { name: name.trim() })
  }

  async function setDescription(id: string, description: string | null) {
    await patchShelf(id, { description })
  }

  async function setColor(id: string, color: string | null) {
    await patchShelf(id, { color })
  }

  /** Añade un libro a una estantería (idempotente). */
  async function addBook(id: string, bookId: string) {
    const shelf = shelves.value.find((s) => s.id === id)
    if (!shelf || shelf.bookIds.includes(bookId)) return
    await patchShelf(id, { bookIds: [...shelf.bookIds, bookId] })
  }

  /** Quita un libro de una estantería. */
  async function removeBook(id: string, bookId: string) {
    const shelf = shelves.value.find((s) => s.id === id)
    if (!shelf || !shelf.bookIds.includes(bookId)) return
    await patchShelf(id, { bookIds: shelf.bookIds.filter((b) => b !== bookId) })
  }

  async function remove(id: string) {
    await repo.deleteShelf(id)
    shelves.value = shelves.value.filter((s) => s.id !== id)
  }

  /** IDs de libros de una estantería, como Set para búsqueda O(1). */
  function bookIdSet(id: string): Set<string> {
    const shelf = shelves.value.find((s) => s.id === id)
    return new Set(shelf?.bookIds ?? [])
  }

  /** Estanterías en las que está un libro dado. */
  const shelvesForBook = computed(() => (bookId: string) =>
    shelves.value.filter((s) => s.bookIds.includes(bookId)),
  )

  return {
    shelves,
    loading,
    loadAll,
    create,
    replaceAll,
    rename,
    setDescription,
    setColor,
    addBook,
    removeBook,
    remove,
    bookIdSet,
    shelvesForBook,
  }
})
